import { flash, SubmitButton } from "@xola/ui-kit";
import { isEqual } from "lodash";
import React, { useMemo } from "react";
import { useMutation } from "react-query";
import {
    CheckoutPreference,
    Experience,
    Form,
    FraudPreference,
    PreCheckProductSelection,
    ProductObject,
    Seller,
} from "types";
import { BotDefenseSection } from "./BotDefenseSection";
import { PreCheckSection } from "./PreCheckSection";
import { ThreeDSecureSection } from "./ThreeDSecureSection";
import { api } from "@/api/api";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { ContentLayout } from "@/components/Layout/ContentLayout";
import { useSeller } from "@/hooks/useAccount";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useInvalidate } from "@/hooks/useInvalidation";
import { getProductsWithPreCheckForms, managePreCheckForms } from "@/models/PreCheckForm";
import { log } from "@/utils/log";

interface FormValues {
    fraud: FraudPreference;
    checkout: CheckoutPreference;
}

const getDefaultFraudPreference = (): FraudPreference => ({
    threeDSecureTriggerAmount: null,
    identityVerification: {
        enabled: false,
        selectedExperiences: {
            all: false,
            experiences: [],
        },
    },
});

const getInitialValues = (seller: Seller): FormValues => {
    const preferences = seller.preferences;
    const existingFraud = preferences?.payment?.fraud;
    const defaultFraud = getDefaultFraudPreference();

    const fraud: FraudPreference = {
        threeDSecureTriggerAmount: existingFraud?.threeDSecureTriggerAmount ?? defaultFraud.threeDSecureTriggerAmount,
        identityVerification: {
            enabled: existingFraud?.identityVerification?.enabled ?? defaultFraud.identityVerification.enabled,
            selectedExperiences:
                existingFraud?.identityVerification?.selectedExperiences ??
                defaultFraud.identityVerification.selectedExperiences,
        },
    };

    const checkout = preferences?.checkout ?? ({ captchaEnabled: false } as CheckoutPreference);

    return { fraud, checkout };
};

export const FraudFighter = () => {
    const seller = useSeller();
    const invalidate = useInvalidate();

    const { data: forms = [] } = useAutoFetchCollection<Form>("forms");
    const { data: experiences = [] } = useAutoFetchCollection<Experience>("experiences");

    // Products that currently have PreCheck forms on the server — the "before" state for managePreCheckForms
    const productsWithPreCheckForms = useMemo(() => getProductsWithPreCheckForms(forms), [forms]);

    const { mutateAsync: updateFraudPreference, isLoading: isSavingFraud } = useMutation(
        async (fraud: FraudPreference) => {
            return api.post(`/users/${seller.id}/preferences/payment`, { fraud });
        },
    );

    const { mutateAsync: updateCheckoutPreference, isLoading: isSavingCheckout } = useMutation(
        async (checkout: CheckoutPreference) => {
            return api.post(`/users/${seller.id}/preferences/checkout`, checkout);
        },
    );

    const isLoading = isSavingFraud || isSavingCheckout;
    const initialValues = useMemo(() => getInitialValues(seller), [seller]);

    const handleSubmit = async (values: FormValues) => {
        try {
            // Convert SelectedExperiences (IDs only) → PreCheckProductSelection[] for managePreCheckForms
            const { enabled, selectedExperiences } = values.fraud.identityVerification;
            const selectedProducts: PreCheckProductSelection[] = enabled
                ? selectedExperiences.all
                    ? experiences.map((e) => ({ id: e.id, object: ProductObject.EXPERIENCE }))
                    : selectedExperiences.experiences.map((id) => ({ id, object: ProductObject.EXPERIENCE }))
                : [];

            await managePreCheckForms(forms, productsWithPreCheckForms, selectedProducts);

            const promises: Promise<unknown>[] = [];

            if (!isEqual(values.fraud, initialValues.fraud)) {
                promises.push(updateFraudPreference(values.fraud));
            }

            if (!isEqual(values.checkout, initialValues.checkout)) {
                promises.push(updateCheckoutPreference(values.checkout));
            }

            if (promises.length > 0) {
                await Promise.all(promises);
            }

            invalidate("sellers", seller.id);
            invalidate("forms");
            flash.success("Successfully saved Fraud Fighter settings");
        } catch (error) {
            log("Error saving Fraud Fighter settings", error);
            flash.error("Failed to save Fraud Fighter settings");
        }
    };

    return (
        <XolaFormik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
            {({ handleSubmit: submitForm, handleReset }) => (
                <ContentLayout isWide>
                    <h2 className="mt-4">Fraud Fighter</h2>
                    <div className="divide-y divide-gray-lighter">
                        <ContentLayout.Section
                            title="Frictionless Bot Defense"
                            description="Blocks fake bookings and spam silently, so real guests can book without solving puzzles"
                        >
                            <BotDefenseSection />
                        </ContentLayout.Section>

                        <ContentLayout.Section
                            title="3D Secure Enforcement"
                            description="Enforcing 3D Secure shifts the liability of fraudulent purchases from the merchant to the buyer"
                        >
                            <ThreeDSecureSection />
                        </ContentLayout.Section>

                        <ContentLayout.Section
                            title="Pre-Check"
                            description="Capture photo ID and live selfie to help fight credit card chargebacks"
                        >
                            <PreCheckSection />
                        </ContentLayout.Section>
                    </div>
                    <ContentLayout.Footer preferenceSidebar>
                        <SubmitButton isLoading={isLoading} onClick={submitForm}>
                            Save
                        </SubmitButton>
                        <SecondaryButton onClick={handleReset}>Cancel</SecondaryButton>
                    </ContentLayout.Footer>
                </ContentLayout>
            )}
        </XolaFormik>
    );
};
